import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useScroll,
} from "motion/react";
import { Menu, X } from "lucide-react";
import logo from "../assets/logo.png";
import { Button } from "./Buttons";
import { EASE } from "../motion";

const LINKS = [
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "gallery", label: "Gallery" },
  { id: "contact", label: "Contact" },
];

export function scrollToId(id) {
  const el = document.getElementById(id);
  const top = el ? el.getBoundingClientRect().top + window.scrollY - 70 : 0;
  window.scrollTo({ top, behavior: "smooth" });
}

/** Tracks which home section is on screen, for the active nav underline. */
function useScrollSpy(enabled) {
  const [active, setActive] = useState(null);
  useEffect(() => {
    if (!enabled) {
      setActive(null);
      return;
    }
    const els = LINKS.map((l) => document.getElementById(l.id)).filter(Boolean);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" },
    );
    els.forEach((el) => io.observe(el));
    const onTop = () => {
      if (window.scrollY < 200) setActive(null);
    };
    window.addEventListener("scroll", onTop, { passive: true });
    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onTop);
    };
  }, [enabled]);
  return active;
}

export default function Nav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onHome = pathname === "/";
  const active = useScrollSpy(onHome);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (y) => setScrolled(y > 24));

  useEffect(() => setOpen(false), [pathname]);

  // Lock page scroll while the mobile menu is open; close on Escape.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const go = (e, id) => {
    e.preventDefault();
    const wasOpen = open;
    setOpen(false);
    if (!onHome) {
      navigate("/#" + id);
      return;
    }
    window.history.replaceState(null, "", "#" + id);
    // Wait for the menu to close (and scroll lock to release) before scrolling,
    // otherwise the browser cancels the smooth scroll.
    setTimeout(() => scrollToId(id), wasOpen ? 320 : 0);
  };

  return (
    <motion.nav
      className={"nav" + (scrolled ? " is-scrolled" : "")}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: EASE }}
    >
      <div className="nav__inner container">
        <Link
          className="nav__brand"
          to="/"
          onClick={() =>
            onHome && window.scrollTo({ top: 0, behavior: "smooth" })
          }
        >
          <motion.img
            src={logo}
            alt="No. 1 Nails & Spa"
            whileHover={{ rotate: -4, scale: 1.05 }}
          />
          <span className="nav__wordmark">NO. 1 NAILS &amp; SPA</span>
        </Link>

        <div className="nav__links">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={"/#" + l.id}
              onClick={(e) => go(e, l.id)}
              className={"nav__link" + (active === l.id ? " is-active" : "")}
            >
              {l.label}
              {active === l.id && (
                <motion.span
                  layoutId="nav-underline"
                  className="nav__underline"
                  transition={{ type: "spring", stiffness: 500, damping: 40 }}
                />
              )}
            </a>
          ))}
          <Button
            to="/booking"
            size="sm"
            className={pathname === "/booking" ? "is-current" : ""}
          >
            Book Now
          </Button>
          <button
            className="nav__toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((o) => !o)}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={open ? "x" : "m"}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {open ? <X /> : <Menu />}
              </motion.span>
            </AnimatePresence>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              className="nav__backdrop"
              onClick={() => setOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              aria-hidden="true"
            />
            <motion.div
              key="menu"
              id="mobile-menu"
              className="nav__mobile"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.28, ease: EASE }}
            >
              <motion.ul
                className="container"
                initial="hidden"
                animate="show"
                variants={{
                  show: {
                    transition: { staggerChildren: 0.05, delayChildren: 0.05 },
                  },
                }}
              >
                {LINKS.map((l) => (
                  <motion.li
                    key={l.id}
                    variants={{
                      hidden: { opacity: 0, x: -12 },
                      show: { opacity: 1, x: 0 },
                    }}
                  >
                    <a href={"/#" + l.id} onClick={(e) => go(e, l.id)}>
                      {l.label}
                    </a>
                  </motion.li>
                ))}
                <motion.li
                  variants={{
                    hidden: { opacity: 0, x: -12 },
                    show: { opacity: 1, x: 0 },
                  }}
                >
                  <Link to="/booking" onClick={() => setOpen(false)}>
                    Book online
                  </Link>
                </motion.li>
              </motion.ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
